/**
 * AI Tutor Persona Service
 *
 * Manages AI tutor personalities and learning style adaptations
 * Enhances user engagement by tailoring the AI's teaching style
 *
 * v11.10.0 - AI Tutor Personality Feature
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import logger from '../utils/logger';

// ==================== PERSONA DEFINITIONS ====================

export const TUTOR_PERSONAS = {
  FRIENDLY_MENTOR: {
    id: 'friendly_mentor',
    name: 'Friendly Mentor',
    icon: '🤗',
    description: 'Warm, encouraging, and supportive. Uses positive reinforcement and celebrates your progress.',
    traits: [
      'Encouraging and patient',
      'Uses positive reinforcement',
      'Celebrates small wins',
      'Explains with empathy'
    ],
    systemPrompt: `You are a warm, friendly mentor helping a student learn. Your teaching style is:
- Always encouraging and patient, never judgmental
- Celebrate every small achievement with genuine enthusiasm
- Use positive reinforcement: "Great job!", "You're making progress!", "That's a smart question!"
- When the student makes a mistake, frame it as a learning opportunity: "That's a common misunderstanding, let's work through it together"
- Use a conversational, friendly tone with occasional emojis for warmth
- Share personal anecdotes or real-world examples to make concepts relatable
- End responses with motivational encouragement
- Ask gentle guiding questions rather than directly correcting errors`
  },

  STRICT_TEACHER: {
    id: 'strict_teacher',
    name: 'Strict Teacher',
    icon: '👨‍🏫',
    description: 'Disciplined and structured. Focuses on accuracy, rigor, and deep understanding.',
    traits: [
      'High standards and precision',
      'Structured learning approach',
      'Emphasizes fundamentals',
      'Direct and clear feedback'
    ],
    systemPrompt: `You are a disciplined, rigorous teacher who maintains high standards. Your teaching style is:
- Precise and accurate in all explanations
- Structured and systematic in your approach
- Emphasize mastery of fundamentals before moving to advanced topics
- Provide direct, clear feedback on mistakes: "This is incorrect because..."
- Set high expectations and challenge students to think deeply
- Use formal language and maintain professional boundaries
- Insist on complete understanding before proceeding
- Give homework-style practice problems to reinforce concepts
- Focus on building strong foundations through repetition and practice`
  },

  SOCRATIC_GUIDE: {
    id: 'socratic_guide',
    name: 'Socratic Guide',
    icon: '🤔',
    description: 'Asks thought-provoking questions to help you discover answers on your own.',
    traits: [
      'Asks guiding questions',
      'Encourages critical thinking',
      'Promotes self-discovery',
      'Develops problem-solving skills'
    ],
    systemPrompt: `You are a Socratic teacher who guides through questions. Your teaching style is:
- Never give direct answers; instead, ask thought-provoking questions
- Guide students to discover concepts on their own through inquiry
- When asked a question, respond with 2-3 guiding questions: "What do you think happens if...?", "How would you approach...?"
- Break down complex problems into smaller questions
- Encourage critical thinking: "Why do you think that is?", "What evidence supports this?"
- Validate student reasoning before gently pointing to gaps in logic
- Use the "what if" technique to explore edge cases
- Foster curiosity by connecting topics to broader questions
- Only provide explanations after the student has attempted to reason through the problem`
  },

  BUDDY_PEER: {
    id: 'buddy_peer',
    name: 'Study Buddy',
    icon: '👥',
    description: 'Casual and relatable, like learning with a friend. Uses simple language and fun examples.',
    traits: [
      'Casual and relatable',
      'Uses simple language',
      'Makes learning fun',
      'Shares the learning journey'
    ],
    systemPrompt: `You are a friendly study buddy learning alongside the student. Your teaching style is:
- Use casual, conversational language like texting a friend
- Liberal use of emojis, memes references, and pop culture analogies
- Make learning fun with jokes, puns, and playful explanations
- Say things like "Dude, this is actually pretty cool!" or "Okay, so here's the deal..."
- Admit when concepts are tricky: "Yeah, this part confused me too at first!"
- Use modern slang and relatable comparisons: "Think of it like how Netflix recommends shows..."
- Keep explanations short and punchy
- Use lots of analogies to everyday situations
- Create a sense of camaraderie: "We got this!", "Let's figure this out together!"
- Make mistakes feel okay: "Happens to everyone, no biggie!"`
  },

  EXPERT_PROFESSOR: {
    id: 'expert_professor',
    name: 'Expert Professor',
    icon: '🎓',
    description: 'Highly knowledgeable and comprehensive. Provides in-depth explanations with academic rigor.',
    traits: [
      'Deep subject expertise',
      'Comprehensive explanations',
      'Academic and thorough',
      'Connects to advanced concepts'
    ],
    systemPrompt: `You are a distinguished professor with deep expertise. Your teaching style is:
- Provide comprehensive, in-depth explanations with academic rigor
- Use precise technical terminology and explain it thoroughly
- Reference academic context, historical development, and real research
- Connect topics to advanced concepts and interdisciplinary applications
- Structure responses like a lecture: introduction, body, conclusion
- Cite principles, theorems, and established knowledge
- Provide multiple perspectives on complex topics
- Include nuances, exceptions, and edge cases
- Encourage exploration of primary sources and advanced materials
- Use formal academic language while remaining accessible
- Provide extensive examples and derivations when explaining concepts`
  }
};

// ==================== LEARNING STYLES ====================

export const LEARNING_STYLES = {
  VISUAL: {
    id: 'visual',
    name: 'Visual Learner',
    icon: '👁️',
    description: 'Learns best through diagrams, charts, and visual representations',
    adaptations: [
      'Use diagrams and visual descriptions',
      'Create mental images and analogies',
      'Suggest drawing concepts',
      'Use spatial reasoning'
    ],
    promptModifier: `The student is a VISUAL LEARNER. Adapt your teaching:
- Describe concepts visually: "Picture this as...", "Imagine a graph where..."
- Use spatial metaphors and visual analogies
- Suggest drawing diagrams, charts, or concept maps
- Describe visual patterns and relationships
- Use formatting (lists, tables, bold/italic) to create visual structure
- Reference colors, shapes, and spatial arrangements in explanations`
  },

  AUDITORY: {
    id: 'auditory',
    name: 'Auditory Learner',
    icon: '👂',
    description: 'Learns best through listening, discussion, and verbal explanations',
    adaptations: [
      'Use conversational tone',
      'Encourage reading aloud',
      'Use rhythmic patterns',
      'Verbal repetition'
    ],
    promptModifier: `The student is an AUDITORY LEARNER. Adapt your teaching:
- Use a conversational, storytelling approach
- Encourage reading concepts aloud or explaining back to you
- Use verbal mnemonics, rhymes, and rhythmic patterns
- Repeat key concepts in different ways for reinforcement
- Suggest discussing topics with others or recording voice notes
- Use dialogue-based examples and verbal walkthroughs`
  },

  READING_WRITING: {
    id: 'reading_writing',
    name: 'Reading/Writing Learner',
    icon: '📝',
    description: 'Learns best through reading texts and writing notes',
    adaptations: [
      'Provide detailed written explanations',
      'Encourage note-taking',
      'Use written examples',
      'Suggest further reading'
    ],
    promptModifier: `The student is a READING/WRITING LEARNER. Adapt your teaching:
- Provide detailed written explanations with clear structure
- Encourage taking notes, summarizing, and writing out concepts
- Use extensive text-based examples and written exercises
- Suggest creating written summaries, lists, and essays
- Reference books, articles, and written resources
- Use written problem-solving steps and detailed derivations`
  },

  KINESTHETIC: {
    id: 'kinesthetic',
    name: 'Kinesthetic Learner',
    icon: '🤸',
    description: 'Learns best through hands-on practice and real-world application',
    adaptations: [
      'Focus on practical examples',
      'Encourage hands-on practice',
      'Use real-world scenarios',
      'Suggest experiments'
    ],
    promptModifier: `The student is a KINESTHETIC LEARNER. Adapt your teaching:
- Focus on hands-on practice and real-world applications
- Use concrete, tangible examples from everyday life
- Suggest experiments, simulations, and interactive activities
- Break concepts into actionable steps: "Try this...", "Now do..."
- Encourage learning by doing rather than passive reading
- Use physical metaphors and movement-based analogies
- Provide practice problems immediately after each concept`
  }
};

// ==================== SYSTEM PROMPT BUILDER ====================

/**
 * Build a customized system prompt based on persona and learning style
 * @param {string} personaId - The selected tutor persona ID
 * @param {string} learningStyleId - The student's learning style ID
 * @param {object} context - Additional context (subject, grade level, etc.)
 * @returns {string} - Complete system prompt for the LLM
 */
export function buildSystemPrompt(personaId, learningStyleId = null, context = {}) {
  const persona = TUTOR_PERSONAS[personaId.toUpperCase()] || TUTOR_PERSONAS.FRIENDLY_MENTOR;
  const learningStyle = learningStyleId ? LEARNING_STYLES[learningStyleId.toUpperCase()] : null;

  let systemPrompt = persona.systemPrompt;

  // Add learning style adaptation
  if (learningStyle) {
    systemPrompt += `\n\n${learningStyle.promptModifier}`;
  }

  // Add contextual information
  if (context.subject) {
    systemPrompt += `\n\nSUBJECT CONTEXT: You are teaching ${context.subject}.`;
  }

  if (context.gradeLevel) {
    systemPrompt += ` The student is in grade ${context.gradeLevel}.`;
  }

  if (context.languagePreference) {
    systemPrompt += ` The student prefers explanations in ${context.languagePreference}.`;
  }

  // Add universal teaching guidelines
  systemPrompt += `\n\nUNIVERSAL GUIDELINES:
- Always be respectful and culturally sensitive
- Adapt difficulty based on student's demonstrated understanding
- If the student seems confused, simplify your explanation
- If the student seems bored, increase the challenge
- Encourage questions and curiosity
- Connect concepts to NCERT curriculum when relevant
- Use Indian cultural context and examples when appropriate`;

  return systemPrompt;
}

// ==================== USER PROFILE MANAGEMENT ====================

/**
 * Get user's learning profile from Firestore
 * @param {string} userId - Firebase user ID
 * @returns {Promise<object>} - User's learning profile
 */
export async function getUserLearningProfile(userId) {
  try {
    const profileRef = doc(db, 'users', userId);
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      const data = profileSnap.data();
      return {
        personaId: data.tutorPersona || 'friendly_mentor',
        learningStyleId: data.learningStyle || null,
        preferences: data.learningPreferences || {},
        quizCompleted: data.learningStyleQuizCompleted || false
      };
    }

    // Return defaults for new users
    return {
      personaId: 'friendly_mentor',
      learningStyleId: null,
      preferences: {},
      quizCompleted: false
    };
  } catch (error) {
    logger.error('❌ Error fetching learning profile:', error);
    return {
      personaId: 'friendly_mentor',
      learningStyleId: null,
      preferences: {},
      quizCompleted: false
    };
  }
}

/**
 * Save user's learning profile to Firestore
 * @param {string} userId - Firebase user ID
 * @param {object} profile - Learning profile data
 */
export async function saveUserLearningProfile(userId, profile) {
  try {
    const profileRef = doc(db, 'users', userId);
    const updateData = {
      tutorPersona: profile.personaId,
      learningStyle: profile.learningStyleId,
      learningPreferences: profile.preferences || {},
      learningStyleQuizCompleted: profile.quizCompleted || false,
      lastUpdated: new Date().toISOString()
    };

    // Check if document exists
    const profileSnap = await getDoc(profileRef);
    if (profileSnap.exists()) {
      await updateDoc(profileRef, updateData);
    } else {
      await setDoc(profileRef, updateData, { merge: true });
    }

    logger.success('✅ Learning profile saved successfully');
  } catch (error) {
    logger.error('❌ Error saving learning profile:', error);
    throw error;
  }
}

/**
 * Update only the tutor persona
 * @param {string} userId - Firebase user ID
 * @param {string} personaId - New persona ID
 */
export async function updateTutorPersona(userId, personaId) {
  try {
    const profileRef = doc(db, 'users', userId);
    await updateDoc(profileRef, {
      tutorPersona: personaId,
      lastUpdated: new Date().toISOString()
    });
    logger.success(`✅ Tutor persona updated to: ${personaId}`);
  } catch (error) {
    logger.error('❌ Error updating tutor persona:', error);
    throw error;
  }
}

/**
 * Update only the learning style
 * @param {string} userId - Firebase user ID
 * @param {string} learningStyleId - New learning style ID
 */
export async function updateLearningStyle(userId, learningStyleId) {
  try {
    const profileRef = doc(db, 'users', userId);
    await updateDoc(profileRef, {
      learningStyle: learningStyleId,
      learningStyleQuizCompleted: true,
      lastUpdated: new Date().toISOString()
    });
    logger.success(`✅ Learning style updated to: ${learningStyleId}`);
  } catch (error) {
    logger.error('❌ Error updating learning style:', error);
    throw error;
  }
}

// ==================== LEARNING STYLE QUIZ ====================

export const LEARNING_STYLE_QUIZ = [
  {
    id: 1,
    question: 'When learning something new, I prefer to:',
    options: [
      { text: 'Watch demonstrations or look at diagrams', style: 'visual', points: 3 },
      { text: 'Listen to explanations or discuss with others', style: 'auditory', points: 3 },
      { text: 'Read detailed instructions or take notes', style: 'reading_writing', points: 3 },
      { text: 'Try it myself and learn by doing', style: 'kinesthetic', points: 3 }
    ]
  },
  {
    id: 2,
    question: 'When I need to remember something, I:',
    options: [
      { text: 'Visualize it in my mind or draw a picture', style: 'visual', points: 3 },
      { text: 'Say it out loud or create a rhyme', style: 'auditory', points: 3 },
      { text: 'Write it down multiple times', style: 'reading_writing', points: 3 },
      { text: 'Associate it with a physical action', style: 'kinesthetic', points: 3 }
    ]
  },
  {
    id: 3,
    question: 'In class, I learn best when the teacher:',
    options: [
      { text: 'Uses slides, charts, and visual aids', style: 'visual', points: 3 },
      { text: 'Explains verbally and encourages discussion', style: 'auditory', points: 3 },
      { text: 'Provides detailed handouts and reading materials', style: 'reading_writing', points: 3 },
      { text: 'Includes experiments, activities, or demos', style: 'kinesthetic', points: 3 }
    ]
  },
  {
    id: 4,
    question: 'When solving a math problem, I prefer to:',
    options: [
      { text: 'Draw diagrams or visualize the solution', style: 'visual', points: 3 },
      { text: 'Talk through the steps out loud', style: 'auditory', points: 3 },
      { text: 'Write out all the steps carefully', style: 'reading_writing', points: 3 },
      { text: 'Use objects or my fingers to work it out', style: 'kinesthetic', points: 3 }
    ]
  },
  {
    id: 5,
    question: 'When studying for an exam, I:',
    options: [
      { text: 'Look at diagrams, charts, and color-code notes', style: 'visual', points: 3 },
      { text: 'Explain concepts aloud or study with friends', style: 'auditory', points: 3 },
      { text: 'Read and re-read my notes', style: 'reading_writing', points: 3 },
      { text: 'Make flashcards and practice problems', style: 'kinesthetic', points: 3 }
    ]
  }
];

/**
 * Calculate learning style from quiz results
 * @param {Array<string>} answers - Array of selected learning styles from each question
 * @returns {string} - Dominant learning style ID
 */
export function calculateLearningStyle(answers) {
  const scores = {
    visual: 0,
    auditory: 0,
    reading_writing: 0,
    kinesthetic: 0
  };

  // Count occurrences of each style
  answers.forEach(answer => {
    if (scores.hasOwnProperty(answer)) {
      scores[answer] += 3;
    }
  });

  // Find the style with highest score
  let maxScore = 0;
  let dominantStyle = 'visual';

  Object.entries(scores).forEach(([style, score]) => {
    if (score > maxScore) {
      maxScore = score;
      dominantStyle = style;
    }
  });

  return dominantStyle;
}

export default {
  TUTOR_PERSONAS,
  LEARNING_STYLES,
  LEARNING_STYLE_QUIZ,
  buildSystemPrompt,
  getUserLearningProfile,
  saveUserLearningProfile,
  updateTutorPersona,
  updateLearningStyle,
  calculateLearningStyle
};
